<script setup lang="ts">
import type { SuggestionVoteResult, SuggestionVoteValue } from '~~/shared/utils/suggestions'

interface Props {
  suggestionId: string
  score: number
  upvotes: number
  downvotes: number
  myVote: SuggestionVoteValue
  size?: 'sm' | 'md'
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md'
})

const emit = defineEmits<{
  voted: [result: SuggestionVoteResult]
}>()

const user = useSupabaseUser()
const route = useRoute()
const toast = useToast()

// Optimistic overlay while the request runs; props take over once the parent
// applies the server's counts (or again when the request fails).
const optimistic = ref<{ vote: SuggestionVoteValue, score: number } | null>(null)
const pending = ref(false)

const currentVote = computed(() => optimistic.value?.vote ?? props.myVote)
const currentScore = computed(() => optimistic.value?.score ?? props.score)

const tooltip = computed(() => `${props.upvotes} upvote${props.upvotes === 1 ? '' : 's'}, ${props.downvotes} downvote${props.downvotes === 1 ? '' : 's'}`)

async function vote(direction: 1 | -1) {
  if (!user.value?.sub) {
    toast.add({
      title: 'Sign in to vote',
      description: 'Voting is open to everyone with a mychar.gg account.',
      icon: 'i-lucide-log-in',
      actions: [{ label: 'Sign in', color: 'neutral', variant: 'outline', onClick: () => { navigateTo(loginPathFor(route.fullPath)) } }]
    })
    return
  }
  if (pending.value) return

  const next: SuggestionVoteValue = currentVote.value === direction ? 0 : direction
  optimistic.value = { vote: next, score: currentScore.value - currentVote.value + next }
  pending.value = true

  try {
    const result = await $fetch<SuggestionVoteResult>(`/api/suggestions/${props.suggestionId}/vote`, {
      method: 'POST',
      body: { value: next }
    })
    emit('voted', result)
  } catch (err) {
    toast.add({
      title: 'Vote not saved',
      description: fetchErrorMessage(err),
      color: 'error',
      icon: 'i-lucide-alert-circle'
    })
  } finally {
    optimistic.value = null
    pending.value = false
  }
}
</script>

<template>
  <UTooltip :text="tooltip">
    <div
      class="flex shrink-0 flex-col items-center rounded-xl border border-default bg-elevated/60"
      :class="size === 'sm' ? 'w-11 py-0.5' : 'w-14 py-1'"
    >
      <UButton
        icon="i-lucide-chevron-up"
        :color="currentVote === 1 ? 'primary' : 'neutral'"
        :variant="currentVote === 1 ? 'soft' : 'ghost'"
        :size="size === 'sm' ? 'xs' : 'sm'"
        :aria-label="currentVote === 1 ? 'Remove upvote' : 'Upvote'"
        :aria-pressed="currentVote === 1"
        @click="vote(1)"
      />
      <SuggestionScore
        :score="currentScore"
        :class="size === 'sm' ? 'text-sm' : 'text-base'"
      />
      <UButton
        icon="i-lucide-chevron-down"
        :color="currentVote === -1 ? 'error' : 'neutral'"
        :variant="currentVote === -1 ? 'soft' : 'ghost'"
        :size="size === 'sm' ? 'xs' : 'sm'"
        :aria-label="currentVote === -1 ? 'Remove downvote' : 'Downvote'"
        :aria-pressed="currentVote === -1"
        @click="vote(-1)"
      />
    </div>
  </UTooltip>
</template>
